import React, { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { 
  Star, 
  MessageSquare, 
  Filter,
  Copy,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Calendar
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const REVIEWS_PER_PAGE = 50;

const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [period, setPeriod] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);

  const periodLabels = {
    day: "Aujourd'hui",
    month: "Ce mois",
    year: "Cette année",
    all: "Tout"
  };

  useEffect(() => {
    fetchReviews();
  }, [period]);

  // Reset to page 1 when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filter, period]);

  const fetchReviews = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/reviews?period=${period}`);
      setReviews(response.data);
    } catch (error) {
      console.error("Error fetching reviews:", error);
      toast.error("Erreur lors du chargement des avis");
    } finally {
      setLoading(false);
    }
  };

  const filteredReviews = reviews.filter(review => {
    if (filter === "positive") return review.rating >= 4;
    if (filter === "negative") return review.rating < 4;
    return true;
  });

  // Pagination
  const totalPages = Math.ceil(filteredReviews.length / REVIEWS_PER_PAGE);
  const startIndex = (currentPage - 1) * REVIEWS_PER_PAGE;
  const paginatedReviews = filteredReviews.slice(startIndex, startIndex + REVIEWS_PER_PAGE);

  const renderStars = (rating) => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-4 h-4 ${
              star <= rating ? "text-amber-400 fill-amber-400" : "text-slate-200"
            }`}
          />
        ))}
      </div>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="reviews-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-['Plus_Jakarta_Sans'] text-slate-900">
            Gestion des avis
          </h1>
          <p className="text-slate-500 mt-1">
            {reviews.length} avis collectés
          </p>
        </div>
        
        {/* Filter */}
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-slate-400" />
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40 h-10 rounded-xl" data-testid="reviews-filter">
              <SelectValue placeholder="Filtrer" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les avis</SelectItem>
              <SelectItem value="positive">Positifs (≥4★)</SelectItem>
              <SelectItem value="negative">Négatifs (&lt;4★)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Period Filter */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-2 text-sm text-slate-500 mr-2">
          <Calendar className="w-4 h-4" />
          <span>Période :</span>
        </div>
        {Object.entries(periodLabels).map(([key, label]) => (
          <Button
            key={key}
            variant={period === key ? "default" : "outline"}
            size="sm"
            onClick={() => setPeriod(key)}
            className={`rounded-full ${
              period === key 
                ? "bg-violet-600 hover:bg-violet-700" 
                : "hover:bg-slate-100"
            }`}
            data-testid={`period-filter-${key}`}
          >
            {label}
          </Button>
        ))}
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl p-4 border border-slate-100">
          <p className="text-2xl font-bold text-slate-900">{reviews.length}</p>
          <p className="text-sm text-slate-500">Total</p>
        </div>
        <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100">
          <p className="text-2xl font-bold text-emerald-600">
            {reviews.filter(r => r.rating >= 4).length}
          </p>
          <p className="text-sm text-emerald-600">Positifs</p>
        </div>
        <div className="bg-orange-50 rounded-xl p-4 border border-orange-100">
          <p className="text-2xl font-bold text-orange-600">
            {reviews.filter(r => r.rating < 4).length}
          </p>
          <p className="text-sm text-orange-600">Négatifs</p>
        </div>
      </div>

      {/* Reviews List */}
      {paginatedReviews.length > 0 ? (
        <div className="space-y-4">
          {paginatedReviews.map((review) => (
            <div 
              key={review.id} 
              className={`bg-white rounded-2xl p-6 border transition-all hover:shadow-lg ${
                review.rating >= 4 
                  ? "border-l-4 border-l-emerald-500 border-slate-100" 
                  : "border-l-4 border-l-orange-500 border-slate-100"
              }`}
              data-testid={`review-card-${review.id}`}
            >
              {/* Review Header */}
              <div className="flex items-start justify-between gap-4 mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    {renderStars(review.rating)}
                    <span className="text-sm font-medium text-slate-600">
                      {review.rating}/5
                    </span>
                    <span className="text-xs text-slate-400 ml-2">{formatDate(review.created_at)}</span>
                  </div>
                </div>
              </div>

              {/* Review Content */}
              <p className="text-slate-700 leading-relaxed mb-4">{review.comment}</p>

              {/* AI Response */}
              {review.ai_response && (
                <div className="bg-violet-50 rounded-xl p-4 mb-4 border border-violet-100">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <MessageSquare className="w-4 h-4 text-violet-600" />
                      <span className="text-sm font-medium text-violet-700">Réponse IA</span>
                    </div>
                  </div>
                  <p className="text-sm text-violet-800">{review.ai_response}</p>
                  
                  {/* Copy button */}
                  <div className="mt-3 pt-3 border-t border-violet-200 flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      onClick={() => {
                        navigator.clipboard.writeText(review.ai_response);
                        toast.success("Réponse copiée !");
                      }}
                      variant="outline"
                      className="rounded-full text-xs border-violet-300 text-violet-700 hover:bg-violet-100"
                    >
                      <Copy className="w-3 h-3 mr-1" />
                      Copier la réponse
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-2xl p-12 border border-slate-100 text-center">
          <MessageSquare className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">Aucun avis trouvé</h3>
          <p className="text-slate-500">
            {filter === "all" 
              ? "Partagez votre QR Code pour collecter des avis clients"
              : "Aucun avis ne correspond à ce filtre"}
          </p>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between bg-white rounded-xl p-4 border border-slate-100">
          <p className="text-sm text-slate-500">
            {startIndex + 1}-{Math.min(startIndex + REVIEWS_PER_PAGE, filteredReviews.length)} sur {filteredReviews.length} avis
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="rounded-lg"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            
            {/* Page numbers */}
            <div className="flex items-center gap-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                <Button
                  key={page}
                  variant={currentPage === page ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setCurrentPage(page)}
                  className={`w-8 h-8 rounded-lg ${
                    currentPage === page 
                      ? "bg-violet-600 text-white" 
                      : "text-slate-600 hover:bg-slate-100"
                  }`}
                >
                  {page}
                </Button>
              ))}
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="rounded-lg"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reviews;
